/**
 * Agregación de las señales del jugador para el dashboard de KNOW.
 *
 * Todo acá es puro: entra la lista de sesiones tal cual la devuelve
 * `GET /know/me/physiology/sessions` y sale lo que la vista dibuja (KPIs,
 * series para los gráficos, filas de historial). El cálculo científico NO
 * vive acá — `r_cardio`/`r_cardio_eda` los computa
 * `backend/app/services/regulation_metrics.py` con baseline fijo pre-sesión;
 * esto sólo promedia, ordena y cuenta lo que ese módulo ya decidió.
 *
 * Convención de presentación: los índices `r_*` son sigmoides en [0, 1]. En
 * la UI se muestran como índice 0–100 (más alto = mejor regulación) porque
 * "0.62" no le dice nada a un jugador; el valor crudo queda en el detalle
 * por ciclo.
 */

import type {
  PhysiologySessionEntry,
  RegulationCycle,
  SignalState,
} from '../../api/physiology'

/** Un ciclo con su posición dentro de la sesión ya resuelta (el `trial_id` es
 * un uuid: inútil como etiqueta visible). */
export interface CycleView {
  cycle: RegulationCycle
  /** 1-based, en orden temporal dentro de la sesión. */
  index: number
  /** Etiqueta corta para eje/tabla: "C3 · 04:12". */
  label: string
  /** Minuto:segundo de inicio del ciclo dentro de la sesión. */
  offsetLabel: string
  /** Segundos de ventana de recuperación medida. */
  recoverySeconds: number
  hrPeak: number | null
  hrRecoveryEnd: number | null
  baselineRmssdMs: number | null
  recoveryRmssdMs: number | null
}

export interface SessionView {
  id: string
  startedAt: Date | null
  state: SignalState
  status: string
  nTrials: number
  cycles: CycleView[]
  /** Ciclos con `r_cardio` no nulo (los que aportan al promedio). */
  measuredCycles: number
  meanRCardio: number | null
  meanRCardioEda: number | null
  bestRCardio: number | null
  meanHrPeak: number | null
  /** Cociente RMSSD recuperación / RMSSD baseline promediado por ciclo: >1 =
   * el sistema parasimpático rebota por encima de su propio baseline. */
  rmssdRecoveryRatio: number | null
}

export interface SignalsSummary {
  totalSessions: number
  sessionsWithSignal: number
  sessionsProcessing: number
  sessionsWithoutSignal: number
  totalCycles: number
  measuredCycles: number
  /** Índice medio de la última sesión con ciclos medidos. */
  lastMeanRCardio: number | null
  /** Delta contra la sesión medida anterior (mismo índice), null si no hay. */
  deltaVsPrevious: number | null
  bestRCardio: number | null
  meanHrPeak: number | null
  rmssdRecoveryRatio: number | null
}

export interface SignalsModel {
  summary: SignalsSummary
  /** Sesiones ordenadas de la más nueva a la más vieja. */
  sessions: SessionView[]
  /** Sólo las que tienen al menos un ciclo medido, de la más VIEJA a la más
   * nueva (orden cronológico, como lo espera el gráfico de tendencia). */
  measuredSessions: SessionView[]
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function mean(values: number[]): number | null {
  if (values.length === 0) return null
  return values.reduce((acc, v) => acc + v, 0) / values.length
}

function formatOffset(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const mm = Math.floor(total / 60)
  const ss = total % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

function toCycleView(cycle: RegulationCycle, index: number): CycleView {
  const components = cycle.components ?? {}
  const offsetLabel = formatOffset(cycle.cycle_start_ms)
  return {
    cycle,
    index,
    label: `C${index} · ${offsetLabel}`,
    offsetLabel,
    recoverySeconds: Math.max(
      0,
      Math.round((cycle.recovery_end_ms - cycle.peak_end_ms) / 1000),
    ),
    hrPeak: num(components.hr_peak_bpm),
    hrRecoveryEnd: num(components.hr_recovery_end_bpm),
    baselineRmssdMs: num(components.baseline_rmssd_ms),
    recoveryRmssdMs: num(components.recovery_rmssd_ms),
  }
}

function toSessionView(entry: PhysiologySessionEntry): SessionView {
  const cycles = [...entry.cycles]
    .sort((a, b) => a.cycle_start_ms - b.cycle_start_ms)
    .map((cycle, i) => toCycleView(cycle, i + 1))

  const rCardio = cycles
    .map((c) => num(c.cycle.r_cardio))
    .filter((v): v is number => v !== null)
  const rCardioEda = cycles
    .map((c) => num(c.cycle.r_cardio_eda))
    .filter((v): v is number => v !== null)
  const hrPeaks = cycles
    .map((c) => c.hrPeak)
    .filter((v): v is number => v !== null)
  const rmssdRatios = cycles
    .filter(
      (c) =>
        c.recoveryRmssdMs !== null &&
        c.baselineRmssdMs !== null &&
        c.baselineRmssdMs > 0,
    )
    .map((c) => c.recoveryRmssdMs! / c.baselineRmssdMs!)

  return {
    id: entry.id,
    startedAt: entry.started_at ? new Date(entry.started_at) : null,
    state: entry.state,
    status: entry.status,
    nTrials: entry.n_trials,
    cycles,
    measuredCycles: rCardio.length,
    meanRCardio: mean(rCardio),
    meanRCardioEda: mean(rCardioEda),
    bestRCardio: rCardio.length > 0 ? Math.max(...rCardio) : null,
    meanHrPeak: mean(hrPeaks),
    rmssdRecoveryRatio: mean(rmssdRatios),
  }
}

export function buildSignalsModel(
  entries: PhysiologySessionEntry[],
): SignalsModel {
  const sessions = entries
    .map(toSessionView)
    .sort((a, b) => (b.startedAt?.getTime() ?? 0) - (a.startedAt?.getTime() ?? 0))

  // Cronológico ascendente y sólo con dato: es el eje de la tendencia.
  const measuredSessions = [...sessions]
    .filter((s) => s.measuredCycles > 0)
    .reverse()

  const last = measuredSessions[measuredSessions.length - 1] ?? null
  const previous = measuredSessions[measuredSessions.length - 2] ?? null
  const allMeasuredCycles = sessions.flatMap((s) =>
    s.cycles.filter((c) => num(c.cycle.r_cardio) !== null),
  )

  return {
    summary: {
      totalSessions: sessions.length,
      sessionsWithSignal: measuredSessions.length,
      sessionsProcessing: sessions.filter((s) => s.state === 'processing').length,
      sessionsWithoutSignal: sessions.filter((s) => s.state === 'no_signal').length,
      totalCycles: sessions.reduce((acc, s) => acc + s.cycles.length, 0),
      measuredCycles: allMeasuredCycles.length,
      lastMeanRCardio: last?.meanRCardio ?? null,
      deltaVsPrevious:
        last?.meanRCardio != null && previous?.meanRCardio != null
          ? last.meanRCardio - previous.meanRCardio
          : null,
      bestRCardio:
        allMeasuredCycles.length > 0
          ? Math.max(...allMeasuredCycles.map((c) => c.cycle.r_cardio as number))
          : null,
      meanHrPeak: mean(
        sessions
          .map((s) => s.meanHrPeak)
          .filter((v): v is number => v !== null),
      ),
      rmssdRecoveryRatio: mean(
        sessions
          .map((s) => s.rmssdRecoveryRatio)
          .filter((v): v is number => v !== null),
      ),
    },
    sessions,
    measuredSessions,
  }
}

/** Sigmoide [0,1] → índice 0–100 para mostrar. */
export function toIndex100(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null
  return Math.round(value * 100)
}

/** Chip de estado tal como se muestra por fila. No es igual al `state` del
 * backend en un caso: una sesión puede tener ciclos detectados y ninguno con
 * dato suficiente (`measured` + 0 medidos) — mostrar "Con señal" ahí sería
 * mentir, así que se degrada a "Ciclos sin dato". */
export function sessionChip(session: SessionView): {
  label: string
  className: string
  help: string
} {
  if (session.state === 'measured' && session.measuredCycles === 0) {
    return {
      label: 'Ciclos sin dato',
      className: 'tag opacity-70',
      help: 'Se detectaron ciclos de estímulo/recuperación, pero ninguno tuvo muestras suficientes para calcular el índice. Abrí el detalle para ver el motivo de cada uno.',
    }
  }
  return {
    label: STATE_LABELS[session.state],
    className: stateTagClass(session.state),
    help: STATE_HELP[session.state],
  }
}

export const STATE_LABELS: Record<SignalState, string> = {
  measured: 'Con señal',
  no_cycles: 'Sin ciclos',
  processing: 'Procesando',
  no_signal: 'Sin sensores',
}

export const STATE_HELP: Record<SignalState, string> = {
  measured: 'Se midieron ciclos de estímulo + recuperación en esta sesión.',
  no_cycles:
    'El procesamiento corrió pero no encontró ciclos de estímulo/recuperación (sesión sin tiros o cortada antes del primero).',
  processing:
    'Los datos de los sensores llegaron y el registro científico todavía se está procesando. Volvé más tarde.',
  no_signal:
    'Esta sesión se jugó sin el Polar puesto, así que no hay señales para medir. No hay nada que esperar.',
}

export function stateTagClass(state: SignalState): string {
  if (state === 'measured') return 'tag-ok'
  if (state === 'processing') return 'tag'
  return 'tag opacity-70'
}

/** Fecha corta y estable para ejes y tablas. */
export function formatSessionDate(date: Date | null): string {
  if (!date) return '—'
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

export function formatSessionDateTime(date: Date | null): string {
  if (!date) return '—'
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
}
